import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrdersService } from '../services/orders.service';
import { SnackBarService } from './../../../services/snack-bar.service';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import {
  Holidays as holidaysOptions,
  OrderOptions as orderNames,
  Parashot as parashotOptions,
  type OrderOption,
  type ParashaOption,
} from '@shared/constants';
import { Location } from '@angular/common';
import { Observable, of } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { OrderType } from '@shared/schemas/orderSchema';
import { ConfirmDialogComponent } from '../../../components/buttons/delete-button-with-icon/confirm-dialog/confirm-dialog.component';

export type OrderNamesType = OrderOption;
export type OptionType = ParashaOption;

@Component({
  selector: 'app-edit-order',
  templateUrl: './edit-order.component.html',
  styleUrls: ['./edit-order.component.scss'],
})
export class EditOrderComponent implements OnInit {
  @ViewChild('confirmDialog') confirmDialog!: ConfirmDialogComponent;

  orderId = '';
  orderForm!: FormGroup;
  loading = false;
  doneBy = '';
  userName = '';
  createdAt = '';
  updatedAt = '';
  names: readonly OrderNamesType[] = orderNames;

  parashaFilterCtrl = new FormControl<OptionType | null>(null);
  filteredParashotOptions: Observable<OptionType[]> = of([]);
  filteredHolidaysOptions: Observable<OptionType[]> = of([]);

  parashotOptions: readonly OptionType[] = parashotOptions;
  holidayOptions: readonly OptionType[] = holidaysOptions;

  // ✅ delete action
  deleteAction: (() => Promise<void>) | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ordersService: OrdersService,
    private snackBar: SnackBarService,
    private fb: FormBuilder,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.orderId = this.route.snapshot.paramMap.get('orderId') ?? '';
    this.initForm();
    this.loadOrder();

    this.filteredParashotOptions = this.parashaFilterCtrl.valueChanges.pipe(
      startWith<OptionType | null>(null),
      map((value) => this.filterOptions(value, this.parashotOptions))
    );

    this.filteredHolidaysOptions = this.parashaFilterCtrl.valueChanges.pipe(
      startWith<OptionType | null>(null),
      map((value) => this.filterOptions(value, this.holidayOptions))
    );
  }

  initForm(): void {
    this.orderForm = this.fb.group({
      price: [0, [Validators.required, Validators.min(0)]],
      pricePaid: [0, [Validators.required, Validators.min(0)]],
      name: ['', Validators.required],
      parasha: ['', Validators.required],
      orderInvoice: [''],
      current_balance: [{ value: 0, disabled: true }],
    });
  }

  private filterOptions(value: OptionType | string | null, options: readonly OptionType[]): OptionType[] {
    const filterValue =
      typeof value === 'string' ? value.toLowerCase() : value?.heb.toLowerCase() ?? '';
    return options.filter(
      (option) =>
        option.heb.toLowerCase().includes(filterValue) ||
        option.eng.toLowerCase().includes(filterValue)
    );
  }

  displayFn(option: OptionType | string | null): string {
    if (!option) return '';
    return typeof option === 'string' ? option : option.heb;
  }

 selectOption(option: OptionType): void {
  const control = this.orderForm.get('parasha');
  control?.setValue(option.heb);
  control?.markAsDirty();   // ✅ mark as dirty
  this.parashaFilterCtrl.setValue(option);
}

  async loadOrder(): Promise<void> {
    this.loading = true;
    try {
      const order: OrderType = await this.ordersService.getOrderById(this.orderId);

      this.doneBy = order.doneBy ?? 'לא ידוע';
      this.userName = order.userName ?? '';
      const date = new Date(order.createdAt);
      this.createdAt = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear().toString().slice(-2)}, ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
      if (order.updatedAt) {
        const updatedDate = new Date(order.updatedAt);
        this.updatedAt = `${updatedDate.getDate()}.${updatedDate.getMonth() + 1}.${updatedDate.getFullYear().toString().slice(-2)}, ${updatedDate.getHours()}:${String(updatedDate.getMinutes()).padStart(2, '0')}`;
      }

      const parashaSelected =
        this.parashotOptions.find((p) => p.heb === order.parasha) ??
        this.holidayOptions.find((h) => h.heb === order.parasha) ??
        null;

      this.orderForm.patchValue({
        price: order.price,
        pricePaid: order.pricePaid,
        name: order.name,
        parasha: parashaSelected?.heb ?? '',
        orderInvoice: order.orderInvoice ?? '',
        current_balance: order.current_balance ?? 0,
      });

      this.parashaFilterCtrl.setValue(parashaSelected);
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.location.back();
  }

  async save(): Promise<void> {
    if (this.orderForm.invalid) return;

    this.loading = true;
    try {
      const newOrder: OrderType = { ...this.orderForm.value, updatedAt: new Date() };
      const res = await this.ordersService.updateOrder(this.orderId, newOrder);
      if (res.success) {
        this.snackBar.openSnackBar('נשמר בהצלחה', 'x');
      }
    } catch (err) {
      console.error(err);
      this.snackBar.openSnackBarError('לא ניתן לשמור את ההזמנה', 'x');
    } finally {
      this.loading = false;
    }
  }

 openDeleteModal(): void {
  this.deleteAction = this.getDeleteAction(this.orderId);
  this.confirmDialog.openModal();
}

getDeleteAction(orderId: string): () => Promise<void> {
  return () => this.onDelete(orderId);
}

async onDelete(orderId: string): Promise<void> {
  this.loading = true;
  try {
    const res = await this.ordersService.deleteOrder(orderId);
    this.snackBar.openSnackBar('נמחק בהצלחה', 'x');

    // Navigate back after deletion
    this.location.back();
  } catch (error) {
    console.error(error);
    this.snackBar.openSnackBarError('לא ניתן למחוק', 'x');
  } finally {
    this.loading = false;
  }
}
}